import { ValidationObj } from "./Grant";
import { DematAccount } from "./VestingTemplate";

export type EmployeesOverviewDto = {
  employees: Employee[];
  departmentEmployeeCountDtos: DepartmentEmployeeCountDto[];
  yearofJoiningEmployeeCountDtos: YearofJoiningEmployeeCountDto[];
};

export interface UploadedData extends EmployeeUploadHeaderResponse {
  newEmployeesToBeAdded: NewEmployeesToBeAdded[];
  duplicateEmployees: DuplicateEmployee[];
}

export interface NewEmployeesToBeAdded {
  "Sl no": number;
  "Employee ID": string;
  "Employee Name": string;
  "Legal Name": string;
  "Status of employment": string;
  "Email address": string;
  "Employment Type": string;
  "Country Residing": string;
  "Registered address": string;
  Manager: string;
  "Employer Name": string;
  Grade: string;
  Designation: string;
  Department: string;
  Contact: string;
  Nationality: string;
  "Date Of Joining": number;
  "Date of Leaving"?: number;
}

export interface DuplicateEmployee {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  status: string;
  type: string;
  email: string;
  hrId: string;
  isLoginProvided: boolean;
  dateOfJoining: string;
  resignationDate: any;
  lastDay: any;
  companyId: string;
  previousEmployeeId: any;
  isDeactivated: boolean;
  isDeleted: boolean;
}

export type Employee = {
  employeeId?: string;
  id: string;
  employeeName: string;
  employeeIdentificationString: string;
  employeeDesignation: string;
  employmentStatus: string;
  department: string;
  email: string;
  totalOptionsGranted?: number;
  totalPercentageVested: number;
  lastDay?: string;
  dateOfJoin: string;
  isLoginProvided?: boolean;
  lastLogin?: string;
  employmentType: string;
  createdAt?: string;
  updatedAt?: string;
  noOfGrants?: number;
  salary?: number;
  isDeactivated: boolean;
  grade?: string;
  name?: string;
  isResignationSchedule?: boolean;
  pausedGrants?: number;
  allGrants?: { identifier: string; state: string }[];
  tags?: { label: string; value: string }[];
  keyPerson?: boolean;
  companyName?: string;
  resignationDate?: string;
  userEmail?: string;
};

export type BulkResignationDto = {
  hrId: string;
  employeeName: string;
  resignationDate?: string;
  lastWorkingDate?: string;
  pauseVesting: string;
  pauseVestingDate?: string;
  lapsedImmediately: string;
  warnings: string;
  errors: string;
  note: string;
};

export type DepartmentEmployeeCountDto = {
  department: string;
  numberOfEmployees: number;
};

export type YearofJoiningEmployeeCountDto = {
  yearofJoining: number;
  numberOfEmployees: number;
};

export interface AddEmployeeReq {
  employeeName: string;
  employeeDesignation: string;
  address: string;
  contactNumber?: string;
  department: string;
  email: string;
  manager: string;
  residentialStatus: string;
  nationality: string;
  legalName: string;
  employeeIdentification: string;
  dateOfJoin: string;
  grade: string;
  employmentStatus: EmployementStatus;
  company: string;
  lastDay?: string;
  notes: string;
  employmentType: EmployementType;
  imageURL?: string;
  pan: string;
  personalEmail?: string;
  dematAccounts?: DematAccount[];
  id?: string;
  subsidiaryCompanyId?: string;
  salary?: number;
  resignationDate?: string;
  employeeCount?: number; //Use only for modelling
  tags?: { label: string; value: string }[];
}

export enum EmployementStatus {
  EMPLOYED = "EMPLOYED",
  RESIGNED = "RESIGNED",
  RESIGNATION_SCHEDULED = "RESIGNATION SCHEDULED",
  PENDING_RESIGNATION = "PENDING RESIGNATION",
  RETIRED = "RETIRED",
  TERMINATED = "TERMINATED",
  PENDING = "PENDING",
}

export enum EmployementType {
  EMPLOYEE = "EMPLOYEE",
  CONSULTANT = "CONSULTANT",
}

export interface RevokeAccessRequest {
  empId: string;
  isDeactived: boolean;
}

export interface RevokeAccessResponse {
  reason: string;
}

export interface UploadEmployeeExcel {
  file?: string;
  correctedGrantData?: ValidationObj[][];
  confirm?: boolean;
}

export interface UploadBulkResignationExcel {
  file: string;
}

export interface BulkResignationImportDto {
  file?: string;
  resignationValidatedData?: ValidationObj[][];
  exerciseValidatedData?: ValidationObj[][];
  confirm?: boolean;
}

export interface GrantReversionWarning {
  grantIdentifier: string;
  dateOfGrant: Date;
  optionsToRevert: number;
}

export interface ResignationRowReversionWarning {
  rowIndex: number;
  hrId: string;
  affectedGrants: GrantReversionWarning[];
}

export interface BulkResignationImportResponse {
  hasErrors: boolean;
  status: boolean;
  message: string;
  resignationValidationData: ValidationObj[][];
  exerciseValidationData: ValidationObj[][];
  reversionWarnings?: ResignationRowReversionWarning[];
  summary?: { resignationsInitiated: number; exercisesCreated: number };
}
export interface UploadTaxExcel extends UploadEmployeeExcel {
  validatedTaxDetail?: ValidationObj[][];
}
export interface UploadOfferExcel {
  file: string;
  eventId: string;
}

export interface UploadGrantExcel {
  file: string;
}

export type EmployeeCredentialGeneration = {
  employeeIds: string[];
  regenerateExisting: boolean;
  sendCredentialsGenerationEmail: boolean;
};

export type EmployeeUploadHeaderResponse = {
  headersInFile: string[];
  headersInSystem: string[];
  message?: string;
  sampleData?: { [k: string]: any };
  validationErrors?: any;
  cacheId?: string;
  validationData?: ValidationResponse[][];
  validationPending?: boolean;
  savePending?: boolean;
};

export type ValidateAndImportResponse = {
  hasErrors: boolean;
  validationData: ValidationResponse[][];
  validationPending: boolean;
};
export type ValidationResponse = {
  name: string;
  value: string | number;
  error: string | null;
  valid: boolean;
  warning?: string;
};
export type ImportConfig = {
  id: string;
  name?: string;
  mapperData: {
    [k: string]: { type: string; field: string };
  };
};

export type RevertResignation = {
  employeeIds: string[];
  vestAsPerToday: boolean;
  sendEmailForVestings: boolean;
  resumeFutureVestings: boolean;
  revertAccessBackToPrimaryEmailId: boolean;
  note: string;
  validateResignedState?: boolean;
};

export interface EmployeeMetaDataRow {
  id: string;
  hrId?: { old: string; new: string };
  email?: { old: string; new: string };
  personalEmail?: { old: string; new: string };
  designation?: { old: string; new: string };
  grade?: { old: string; new: string };
  address?: { old: string; new: string };
  type?: { old: string; new: string; values?: string[] };
  dateOfJoining?: { old: Date; new: Date };
  subsidiaryCompany?: { old: string; new: string; values?: string[] };
  manager?: { old: string; new: string };
  contact?: { old: string; new: string };
  pan?: { old: string; new: string };
  department?: { old: string; new: string };
  residentialStatus?: { old: string; new: string };
  nationality?: { old: string; new: string };
  workingCountry?: { old: string; new: string };
  salary?: { old: number; new: number };
  isNewRegime?: { old: boolean; new: boolean };
  taxSlab?: { old: string; new: string };
  surchargePercentage?: { old: number; new: number };
  cessPercentage?: { old: number; new: number };
  nominees?: {
    old: {
      nominee1?: NomineeDetails;
      nominee2?: NomineeDetails;
      nominee3?: NomineeDetails;
      nominee4?: NomineeDetails;
      nominee5?: NomineeDetails;
      nominee6?: NomineeDetails;
    };
    new: {
      nominee1?: NomineeDetails;
      nominee2?: NomineeDetails;
      nominee3?: NomineeDetails;
      nominee4?: NomineeDetails;
      nominee5?: NomineeDetails;
      nominee6?: NomineeDetails;
    };
  };
  [key: string]: any;
}

export type NomineeGuardian = {
  name?: string;
  relationship?: string;
  contact?: string;
  email?: string;
  dob?: string | null;
  address?: string;
};

export type NomineeDetails = {
  name?: string;
  percentage?: string | number;
  relationship?: string;
  dob?: string | null;
  address?: string;
  contact?: string;
  email?: string;
  guardian?: NomineeGuardian | null;
};
